import { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    fetchApplicationUsers,
    fetchAdminUsers,
    createApplicationUser,
    createAdminUser,
    updateApplicationUser,
    updateAdminUser,
    deleteAdminUser,
    ApplicationUser,
    AdminUser,
    CreateAdminUserPayload,
    CreateAppUserPayload,
    fetchPdpaConsents,
    fetchPdpaRequests,
    PdpaConsent,
    PdpaRequest,
} from '../../../api/admin';
import { logger } from '../../../utils/logger';

export function useUserManagement() {
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState<'app' | 'admin' | 'security'>('app');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'suspended'>('all');
    const [showModal, setShowModal] = useState<'app' | 'admin' | null>(null);
    const [editAdmin, setEditAdmin] = useState<AdminUser | null>(null);
    const [pdpaExpandedUser, setPdpaExpandedUser] = useState<string | null>(null);
    const [pdpaLoadingUser, setPdpaLoadingUser] = useState<string | null>(null);
    const [pdpaData, setPdpaData] = useState<Record<string, { consents: PdpaConsent[]; requests: PdpaRequest[] }>>({});

    // Suspend modal state
    const [suspendModal, setSuspendModal] = useState<{ user: ApplicationUser } | null>(null);
    const [suspendReason, setSuspendReason] = useState('');
    const [suspendReasonDetail, setSuspendReasonDetail] = useState('');

    const appUsersQuery = useQuery({
        queryKey: ['admin-app-users', searchTerm],
        queryFn: () => fetchApplicationUsers({ search: searchTerm || undefined, limit: 100 }),
        enabled: activeTab === 'app',
    });

    const adminUsersQuery = useQuery({
        queryKey: ['admin-users', searchTerm],
        queryFn: () => fetchAdminUsers({ search: searchTerm || undefined, limit: 100 }),
        enabled: activeTab === 'admin',
    });

    const addAppForm = useForm<CreateAppUserPayload>({
        defaultValues: {
            phoneNumber: '',
            firstName: '',
            lastName: '',
            status: 'active',
            loyaltyLevel: 1,
        },
    });

    const addAdminForm = useForm<CreateAdminUserPayload>({
        defaultValues: {
            username: '',
            role: 'staff',
            status: 'active',
            password: '',
        },
    });

    const editAdminForm = useForm<{ username: string; password: string; status: 'active' | 'inactive' }>({
        defaultValues: {
            username: '',
            password: '',
            status: 'active',
        },
    });

    const createAppUserMutation = useMutation({
        mutationFn: (payload: CreateAppUserPayload) => createApplicationUser(payload),
        onSuccess: () => {
            toast.success('เพิ่มผู้ใช้แอปสำเร็จ');
            queryClient.invalidateQueries({ queryKey: ['admin-app-users'] });
            setShowModal(null);
            addAppForm.reset();
        },
        onError: () => toast.error('ไม่สามารถเพิ่มผู้ใช้ได้'),
    });

    const createAdminUserMutation = useMutation({
        mutationFn: (payload: CreateAdminUserPayload) => createAdminUser(payload),
        onSuccess: () => {
            toast.success('สร้างผู้ใช้งานหลังบ้านสำเร็จ');
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            setShowModal(null);
            addAdminForm.reset({ username: '', password: '', role: 'staff', status: 'active' });
        },
        onError: (error: any) => {
            const errorData = error?.response?.data?.error;
            const message = typeof errorData === 'string'
                ? errorData
                : errorData?.message || 'ไม่สามารถเพิ่มพนักงานได้';
            toast.error(message);
        },
    });

    const updateAppUserMutation = useMutation({
        mutationFn: ({ id, status, suspendReason, suspendReasonDetail }: { id: string; status: string; suspendReason?: string; suspendReasonDetail?: string }) =>
            updateApplicationUser(id, { status, suspendReason, suspendReasonDetail }),
        onSuccess: (_data, variables) => {
            if (variables.status === 'suspended') {
                toast.success('ระงับการใช้งานผู้ใช้แล้ว');
            } else if (variables.status === 'active') {
                toast.success('ยกเลิกการระงับผู้ใช้แล้ว');
            } else {
                toast.success('อัปเดตสถานะผู้ใช้แล้ว');
            }
            queryClient.invalidateQueries({ queryKey: ['admin-app-users'] });
        },
        onError: () => toast.error('ไม่สามารถอัปเดตสถานะได้'),
    });

    const updateAdminStatusMutation = useMutation({
        mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) => updateAdminUser(id, { status }),
        onSuccess: () => {
            toast.success('อัปเดตสถานะพนักงานแล้ว');
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
        },
        onError: (error: any) => {
            const errorData = error?.response?.data?.error;
            const message = typeof errorData === 'string'
                ? errorData
                : errorData?.message || 'ไม่สามารถอัปเดตสถานะได้';
            toast.error(message);
        },
    });

    const deleteAdminUserMutation = useMutation({
        mutationFn: (id: string) => deleteAdminUser(id),
        onSuccess: () => {
            toast.success('ลบพนักงานแล้ว');
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
        },
        onError: () => toast.error('ไม่สามารถลบผู้ใช้ได้'),
    });

    const updateAdminDetailsMutation = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateAdminUserPayload> }) => updateAdminUser(id, payload),
        onSuccess: () => {
            toast.success('อัปเดตรายละเอียดพนักงานแล้ว');
            queryClient.invalidateQueries({ queryKey: ['admin-users'] });
            setEditAdmin(null);
        },
        onError: (error: any) => {
            const errorData = error?.response?.data?.error;
            const message = typeof errorData === 'string'
                ? errorData
                : errorData?.message || 'ไม่สามารถอัปเดตรายละเอียดได้';
            toast.error(message);
        },
    });

    const appUsers = useMemo(() => {
        const list = appUsersQuery.data?.users ?? [];
        if (statusFilter === 'all') return list;
        return list.filter((user: ApplicationUser) => user.status === statusFilter);
    }, [appUsersQuery.data, statusFilter]);

    const adminRawUsers = adminUsersQuery.data?.users ?? [];

    const adminUsers = useMemo(() => {
        const list = adminRawUsers.filter((user: AdminUser) => !user.isDefault && user.role === 'staff');
        if (statusFilter === 'all') return list;
        return list.filter((user: AdminUser) => user.status === statusFilter);
    }, [adminRawUsers, statusFilter]);

    const isLoading =
        activeTab === 'app'
            ? appUsersQuery.isLoading
            : activeTab === 'admin'
                ? adminUsersQuery.isLoading
                : false;

    const appTotal = appUsersQuery.data?.total ?? appUsersQuery.data?.users?.length ?? 0;
    const adminTotal = adminUsers.length;

    const handleToggleAppUser = (user: ApplicationUser) => {
        if (user.status === 'active') {
            setSuspendModal({ user });
            setSuspendReason('');
            setSuspendReasonDetail('');
        } else if (user.status === 'suspended') {
            updateAppUserMutation.mutate({ id: user.id, status: 'active' });
        }
    };

    const handleConfirmSuspend = () => {
        if (!suspendModal) return;
        if (!suspendReason) {
            toast.error('กรุณาเลือกเหตุผลในการระงับ');
            return;
        }
        updateAppUserMutation.mutate({
            id: suspendModal.user.id,
            status: 'suspended',
            suspendReason,
            suspendReasonDetail: suspendReasonDetail || undefined,
        });
        setSuspendModal(null);
    };

    const handleToggleAdminUser = (user: AdminUser) => {
        if (user.isDefault) {
            toast.error('ไม่สามารถปิดใช้งานผู้ใช้เริ่มต้นได้');
            return;
        }
        const nextStatus = user.status === 'active' ? 'inactive' : 'active';
        updateAdminStatusMutation.mutate({ id: user.id, status: nextStatus });
    };

    const handleOpenEditAdmin = (user: AdminUser) => {
        setEditAdmin(user);
        editAdminForm.reset({
            username: user.username,
            password: '',
            status: (user.status as 'active' | 'inactive') ?? 'active',
        });
    };

    const handleSubmitEditAdmin = editAdminForm.handleSubmit(values => {
        if (!editAdmin) return;
        const trimmedUsername = values.username.trim();
        if (!trimmedUsername) {
            editAdminForm.setError('username', { type: 'manual', message: 'กรุณากรอกชื่อผู้ใช้' });
            return;
        }
        const payload: Partial<CreateAdminUserPayload> = {
            username: trimmedUsername,
            status: editAdmin.isDefault ? (editAdmin.status as 'active' | 'inactive') : values.status as 'active' | 'inactive',
        };
        if (values.password) {
            payload.password = values.password;
        }
        updateAdminDetailsMutation.mutate({ id: editAdmin.id, payload });
    });

    const handleDeleteAdmin = (user: AdminUser) => {
        if (user.isDefault) {
            toast.error('ไม่สามารถลบผู้ใช้เริ่มต้นได้');
            return;
        }
        if (!window.confirm('ต้องการลบผู้ใช้นี้หรือไม่?')) return;
        deleteAdminUserMutation.mutate(user.id);
    };

    const handleTogglePdpaDetails = async (user: ApplicationUser) => {
        if (pdpaExpandedUser === user.id) {
            setPdpaExpandedUser(null);
            return;
        }

        setPdpaExpandedUser(user.id);

        if (pdpaData[user.id]) {
            return;
        }

        setPdpaLoadingUser(user.id);
        try {
            const [consents, requests] = await Promise.all([
                fetchPdpaConsents({ phone: user.phoneNumber, limit: 10 }),
                fetchPdpaRequests({ phone: user.phoneNumber, limit: 10 }),
            ]);

            setPdpaData(prev => ({
                ...prev,
                [user.id]: {
                    consents,
                    requests,
                },
            }));
        } catch (error) {
            logger.error('PDPA overview error', error);
            toast.error('ไม่สามารถโหลดข้อมูล PDPA');
        } finally {
            setPdpaLoadingUser(null);
        }
    };

    return {
        activeTab,
        setActiveTab,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        showModal,
        setShowModal,
        editAdmin,
        setEditAdmin,
        pdpaExpandedUser,
        pdpaLoadingUser,
        pdpaData,
        suspendModal,
        setSuspendModal,
        suspendReason,
        setSuspendReason,
        suspendReasonDetail,
        setSuspendReasonDetail,
        appUsers,
        adminUsers,
        isLoading,
        appTotal,
        adminTotal,
        handleToggleAppUser,
        handleConfirmSuspend,
        handleToggleAdminUser,
        handleOpenEditAdmin,
        handleSubmitEditAdmin,
        handleDeleteAdmin,
        handleTogglePdpaDetails,
        addAppForm,
        addAdminForm,
        editAdminForm,
        createAppUserMutation,
        createAdminUserMutation,
        updateAppUserMutation,
        deleteAdminUserMutation,
        updateAdminDetailsMutation,
        appUsersQuery,
        adminUsersQuery,
    };
}
